// src/Login.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Paper, TextField, Button, Typography, Alert } from '@mui/material';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useNavigate, useLocation } from 'react-router-dom';


const Login = () => {
  const supabase = useSupabaseClient();
  const navigate = useNavigate();
  const location = useLocation();
  // Keep the latest location in a ref so the once-registered auth listener
  // reads the URL the user is actually on — AuthGate overlays the login card
  // ON TOP of the current URL without changing it — instead of a stale
  // closure value.
  const locationRef = useRef(location);
  locationRef.current = location;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Navigate ONLY after Supabase fires SIGNED_IN — guarantees the auth-helpers
  // SessionContextProvider has the new session in state before we route. The
  // previous "navigate immediately after signInWithPassword resolves" raced the
  // context update; on slow JS engines (mobile in particular) the next route
  // saw a null session, ProtectedRoute bounced back to /login, and the form
  // appeared to do nothing.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        // Return the user to wherever they were headed. On session-expiry
        // re-login AuthGate kept the URL intact, so `location` already points
        // at the intended page. A bare visit to "/" lands on the dashboard
        // (HomeDashboard); only an explicit "/login" URL needs redirecting to
        // the dashboard (it has no page of its own).
        const loc = locationRef.current;
        let dest = `${loc.pathname}${loc.search}${loc.hash}`;
        if (!dest || dest.startsWith('/login')) {
          dest = '/';
        }
        navigate(dest, { replace: true });
      }
    });
    return () => subscription?.unsubscribe();
  }, [supabase, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) {
      setError(signInError.message);
      setSubmitting(false);
    }
    // success path: onAuthStateChange listener above navigates on SIGNED_IN
  };

  return (
    <Paper elevation={12} sx={{ p: 4, borderRadius: 3 }}>
      <Typography variant="h5" align="center" gutterBottom>
        Please sign in
      </Typography>
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      <form onSubmit={handleLogin}>
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          fullWidth
          margin="normal"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="current-password"
          fullWidth
          margin="normal"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={submitting}
          sx={{ mt: 2 }}
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </Paper>
  );
};

export default Login;
